import './PhoneNumberField.css'

import intlTelInput from 'intl-tel-input'
import {
  type ChangeEvent,
  type CSSProperties,
  memo,
  useEffect,
  useRef,
} from 'react'

import { shipperUri } from '@fv/client-core'

import IntlTelInput, { type IntlTelInputRef } from './IntlTelInput.js'
import type { TextFieldProps } from './TextField.jsx'

type Props = TextFieldProps
export const PhoneNumberField = memo(
  ({
    disabled,
    name,
    required,
    onChange,
    value,
    className,
    readOnly,
  }: Props) => {
    const myVal = useRef(value)
    const ref = useRef<IntlTelInputRef>(null)

    useEffect(() => {
      // catch updates from parent that so we can reset validation status & formatting on the input
      if (myVal.current !== value) {
        ref.current?.getInstance()?.setNumber((value ?? '').toString())
      }
      myVal.current = value
    }, [value])

    const handleChange = (num: string) => {
      const iti = ref.current?.getInstance()
      let number = num

      if (!number) {
        iti?.setCountry('us')
      } else {
        const iso2 = iti?.getSelectedCountryData().iso2
        const countryCode = iti?.getSelectedCountryData().dialCode

        number =
          intlTelInput.utils && countryCode === '1'
            ? intlTelInput.utils.formatNumber(
                num,
                iso2,
                intlTelInput.utils.numberFormat.NATIONAL,
              )
            : num
      }
      myVal.current = number
      onChange({
        target: {
          ...(ref.current?.getInput() ?? {}),
          value: number,
        },
      } as ChangeEvent<HTMLInputElement>)
    }

    return (
      <IntlTelInput
        ref={ref}
        initialValue={value as string}
        onChangeValidity={(valid, value) => {
          ref.current
            ?.getInput()
            ?.setCustomValidity(
              valid || (!value && !required)
                ? ''
                : 'Please enter a valid phone number',
            )
        }}
        onChangeNumber={handleChange}
        inputProps={{
          name,
          required,
          readOnly,
          disabled,
          className,
          style: {
            '--iti-right-hand-selected-country-padding': '0px', // overwrite bad css var
          } as CSSProperties,
        }}
        initOptions={{
          showFlags: false,
          allowDropdown: false,
          formatAsYouType: true,
          formatOnDisplay: true,
          containerClass: 'w-full',
          initialCountry: 'us',
          nationalMode: false,
          strictMode: true,
          customPlaceholder: () => '(___) ___-____',
          loadUtils: () =>
            import(`${shipperUri}/intl-tel-input-25.3.1-utils.js`),
        }}
      />
    )
  },
)
